import {Button} from "@headlessui/react";
import {useEffect, useState} from "react";
import {db} from "../../firebase/firebase";
import {ArrowPathIcon, DocumentArrowDownIcon} from "@heroicons/react/24/outline";
import TransactionCard from "../TransactionCard";
import {numberWithCommas} from "../GlobalFunctions";
import * as XLSX from "xlsx";

const Transactions = () => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchTransactions = async () => {
        try {
            setLoading(true);

            const snapshot = await db.collection("Transactions").orderBy("createdAt", "desc").get();

            const data = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
            }));

            setTransactions(data);
        } catch (err) {
            console.error("Error fetching transactions:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTransactions();
    }, []);

    const uniqueShoppers = new Set(
        transactions.map(t => t.shopperId).filter(Boolean)
    ).size;

    const totalItems = transactions.reduce((sum, t) => {
        const items = t.items || [];
        return sum + items.reduce((s, i) => s + (i.orderQuant || 0), 0);
    }, 0);

    const totalValue = transactions.reduce((sum, t) => {
        return sum + (t.total || 0);
    }, 0);

    const stats = [
        {
            label: "Transactions",
            value: transactions.length,
            sub: "Total Transactions",
        },
        {
            label: "Shoppers",
            value: uniqueShoppers,
            sub: "Unique Shoppers",
        },
        {
            label: "Items",
            value: totalItems,
            sub: "Total Items Across Orders",
        },
        {
            label: "Monetary Value",
            value: `$${totalValue.toFixed(2)}`,
            sub: "Total Value of Transactions",
        },
    ];

    const exportTransactions = () => {
        try {
            if (!transactions.length) return;

            const data = transactions.map(t => ({
                ID: t.id,
                Shopper: t.shopperName || "",
                Items: t.items?.length || 0,
                Quantity: t.items?.reduce((sum, i) => sum + (i.orderQuant || 0), 0) || 0,
                Total: t.total || 0,
                Date: t.createdAt
                    ? new Date(t.createdAt).toLocaleDateString()
                    : "",
            }));

            const worksheet = XLSX.utils.json_to_sheet(data);
            const workbook = XLSX.utils.book_new();

            XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");

            XLSX.writeFile(workbook, "transactions.xlsx");
        } catch (err) {
            console.error("Error exporting transactions:", err);
        }
    };

    return (
        <div className="p-4 animate-fadeIn bg-white h-dvh">

            {/*Header*/}
            <div className="grid grid-cols-5 border-b pb-2">

                {/*Stats*/}
                <div className="col-span-4 grid grid-cols-4 gap-2 md:gap-5 divide-y sm:divide-y-0 sm:divide-x divide-line-2">
                    {stats.map((stat, idx) => (
                        <div
                            key={idx}
                            className={`py-4 sm:py-0 ps-1 ${idx === 1 ? "sm:px-4" : ""} ${idx === 2 ? "sm:ps-4" : "sm:pe-4"}`}
                        >
                            <p className="text-sm font-bold">
                                {stat.label}
                            </p>

                            <span className="block text-2xl text-foreground">
                                {numberWithCommas(stat.value)}
                            </span>

                            <p className="-mt-1.5 text-xs text-gray-500 font-semibold">
                                {stat.sub}
                            </p>
                        </div>
                    ))}
                </div>

                {/*Buttons*/}
                <div className="col-span-1 flex items-center justify-end space-x-2 sm:ml-16 sm:mt-0 sm:flex-none">

                    <Button
                        type="button"
                        onClick={exportTransactions}
                        className="flex items-center rounded-md bg-blue-600 px-3 py-1 gap-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    >
                        <DocumentArrowDownIcon className={`size-4`}/>
                        Export
                    </Button>

                    <Button
                        type="button"
                        onClick={fetchTransactions}
                        className="flex items-center rounded-md bg-blue-600 px-3 py-1 gap-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    >
                        <ArrowPathIcon className={`size-4`}/>
                        Refresh
                    </Button>

                </div>

            </div>

            {/*Body*/}
            <div className="flow-root">
                <div className="-mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">


                        <div className="space-y-3">
                            {loading && (
                                <div className="text-center py-6 text-sm text-gray-500">
                                    Loading transactions...
                                </div>
                            )}

                            {!loading && transactions.length === 0 && (
                                <div className="text-center py-6 text-sm text-gray-500">
                                    No transactions found
                                </div>
                            )}

                            {transactions.map((transaction) => (
                                <TransactionCard
                                    key={transaction.id}
                                    transaction={transaction}
                                />
                            ))}
                        </div>


                    </div>
                </div>
            </div>

        </div>
    )
}

export default Transactions;
